import React, { Fragment, FunctionComponent } from "react";
import SpotProps from "./SpotProps.interface";
import styles from "./Spot.module.scss";

import Button from "components/Button/Button";
import combineClasses from "helpers/combine-classes";
import DynamicHeading from "components/DynamicHeading/DynamicHeading";
import getImageAspectRatio from "app/media/helpers/getImageAspectRatio";
import getImageCrop from "app/media/helpers/getImageCrop";
import ImageViewer from "components/ImageViewer/ImageViewer";
import Paragraph from "components/Paragraph/Paragraph";
import VideoViewer from "components/VideoViewer/VideoViewer";
import VideoEmbed from "components/VideoEmbed/VideoEmbed";
import getVideoAspectRatio from "app/media/helpers/getVideoAspectRatio";

const Spot: FunctionComponent<SpotProps> = (props): JSX.Element => {
  // Image
  const image = props.content?.media?.[0]?.content?.image
    ? props.content.media?.[0]?.content
    : undefined;
  const imageCrop = getImageCrop(
    image?.image.umbracoFile.crops,
    image?.imageCrop,
    "Fullscreen (4:3)"
  );
  const imageAspectRatio = getImageAspectRatio(
    imageCrop,
    image?.image.umbracoWidth,
    image?.image.umbracoHeight
  );

  const badgeOverflowClass =
    props.content.badge_primary && props.content.badge_primary.length > 2
      ? styles.has_overflow
      : undefined;

  const overflowCount =
    props.cardSize === "Large" ? 2 : props.cardSize === "Small" ? 6 : 4;
  const hasOverflowClass =
    props.length >= overflowCount
      ? styles[`overflow_${props.cardSize}`]
      : styles.has_few;

  // Video
  const video = props.content?.media?.[0]?.content?.video
    ? props.content.media?.[0]?.content
    : undefined;
  const videoAspectRatio = getVideoAspectRatio(
    video?.video?.[0].content.videoCrop,
    "Widescreen (16:9)"
  );
  const videoSizeClass = video?.videoSize ? styles[video.videoSize] : undefined;

  const isAutoplay =
    video && video?.video?.[0].content.videoAutoplay
      ? video?.video?.[0].content.videoAutoplay
      : false;
  const isLoop =
    video && video?.video?.[0].content.videoLoop
      ? video?.video?.[0].content.videoLoop
      : false;
  const showControls = !isAutoplay;

  return (
    <div className={combineClasses(styles.wrapper, hasOverflowClass)}>
      <div className={combineClasses(styles.inner, videoSizeClass)}>
        {/* Media */}

        {/* Image */}
        {props.content.media &&
        image &&
        image._contentType === "blockImageNestedImage" &&
        image.image?.umbracoFile ? (
          <div className={styles.image_wrapper}>
            {/* Badge */}
            <div className={styles.badge}>
              {props.content.badge_primary ? (
                <strong
                  className={combineClasses(
                    styles.badge_primary,
                    badgeOverflowClass
                  )}
                >
                  {props.content.badge_primary}
                </strong>
              ) : null}

              {props.content.badge_secondary ? (
                <span className={styles.badge_secondary}>
                  {props.content.badge_secondary}
                </span>
              ) : null}
            </div>

            {/* Image */}
            <ImageViewer
              className={styles.image}
              src={image.image.umbracoFile.src}
              focalPoint={image.image.umbracoFile.focalPoint}
              aspectRatioX={imageAspectRatio.aspectRatioX}
              aspectRatioY={imageAspectRatio.aspectRatioY}
            />
          </div>
        ) : null}

        {/* Video */}
        {props.content.media &&
        video &&
        video?._contentType === "blockVideoNestedVideo" ? (
          <Fragment>
            {/* Local Video */}
            {video &&
            video.video?.[0].content._contentType === "blockVideoNestedLocal" &&
            video?.video?.[0].content.video.umbracoFile ? (
              <VideoViewer
                key={props.contentUdi}
                src={video?.video?.[0].content.video.umbracoFile}
                alt={video.videoAlternative}
                autoplay={isAutoplay}
                loop={isLoop}
                controls={showControls}
                aspectRatioX={videoAspectRatio.aspectRatioX}
                aspectRatioY={videoAspectRatio.aspectRatioY}
              />
            ) : null}

            {/* Video Embed */}
            {video &&
            video.video?.[0].content._contentType ===
              "blockVideoNestedYouTube" &&
            video?.video?.[0].content.videoUrl ? (
              <VideoEmbed
                key={props.contentUdi}
                src={video?.video?.[0].content.videoUrl}
                alt={video.videoAlternative}
                autoplay={isAutoplay}
                loop={isLoop}
                aspectRatioX={16}
                aspectRatioY={9}
              />
            ) : null}

            {/* Caption */}
            {video.videoCaption && video.videoCaption.length > 0 ? (
              <div className={styles.caption}>{video.videoCaption}</div>
            ) : null}
          </Fragment>
        ) : null}

        {/* Content */}
        <div className={styles.content}>
          {props.content.heading ? (
            <DynamicHeading
              className={styles.heading}
              headlineSize={"H6"}
              headlineWeight={"H6"}
            >
              {props.content.heading}
            </DynamicHeading>
          ) : null}

          {props.content.bodyText ? (
            <Paragraph
              className={styles.text}
              innerHTML={props.content.bodyText}
            />
          ) : null}

          {/* Buttons */}
          {props.content.buttons && props.content.buttons.length > 0 ? (
            <div className={styles.buttons}>
              {props.content.buttons.map((button, index) => {
                return (
                  <Button
                    key={index}
                    to={button.content.link}
                    variant={button.content.variant}
                    {...button.content}
                  >
                    {button.content.label}
                  </Button>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default Spot;
